import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { auth, config } from '../config';
import { EventType, TrackingItems } from 'shared-ui-components';
import { NewLoanInProgressProps } from '@components/types';
import { logApiEvent, logUserEvent } from '@analytics/amplifyLogging';
import { NEW_LOAN_IN_PROGRESS_WIDGETS } from '@utils/appConstants';
import { useUserDetails } from '@hooks/userDetails';
import { NewLoanInProgressConfettiWrapper, PreviewLink, WidgetContentContainer } from '@app.styled';
import { Confetti } from '@components/Confetti/Confetti';
import { dispatch, RootState } from '@store';
import { useSelector } from 'react-redux';
import { setESIGNPOPUPSTATUS, setParentModal } from '@store/actions/lip.action';

export const NewLoanInProgressContainer: React.FC<NewLoanInProgressProps> = ({ loanNumber, nlpWidgetList }) => {
    const token = auth.getOkta().getAccessToken();
    const selfServUrl = config.env('selfServUrl');
    const xApiKey = config.env('xApiKey');
    const [isConfettiNeeded, setIsConfettiNeeded] = useState(false);
    const [userInfo] = useUserDetails();
    const widgets = useMemo(() => {
        return NEW_LOAN_IN_PROGRESS_WIDGETS.map(cWidgetName => {
            return nlpWidgetList.includes(cWidgetName) ? cWidgetName : null;
        }).filter(Boolean);
    }, [nlpWidgetList]);

    const inputRef = useRef(null);
    const eSignRef = useRef(null);
    const [fileSrc, setFileSrc] = useState('');

    const status = useSelector((state: RootState) => state.notification.result);
    useEffect(() => {
        if (fileSrc) {
            inputRef.current?.click();
        }
    }, [fileSrc]);

    useEffect(() => {
        setTimeout(() => {
            window.scroll(0, 0);
        }, 500);
    }, []);

    const onPreview = (file: any) => {
        setFileSrc(URL.createObjectURL(file));
    };

    const props2: any = {
        loanNumber,
        authToken: token,
        widgets,
        onPreview,
        userName: userInfo?.given_name,
        setConfettiExplosion: () => setIsConfettiNeeded(true),
        selfServUrl: selfServUrl,
        xApiKey: xApiKey
    };

    const apiEvents = useCallback((event: EventType) => {
        logApiEvent({
            apiName: event.apiName,
            action: event.action,
            correlationId: event.correlationId,
            timestamp: event.timestamp,
            loanNumber: event.loanNumber,
            description: event.description,
            type: 'New Loan In Progress widget'
        });
    }, []);

    const userEvents = useCallback((event: EventType) => {
        logUserEvent({
            type: 'New Loan In Progress widget',
            action: event.action,
            loanNumber: event.loanNumber,
            description: event.description
        });
    }, []);

    const handleTokenResponse = useCallback((token: string, url: string) => {
        dispatch(setParentModal({ showModal: true, component: 'esign', payload: { token, url } }));
        dispatch(setESIGNPOPUPSTATUS('opened'));
    }, []);

    useEffect(() => {
        if (status === 'closed' && eSignRef && eSignRef.current) {
            eSignRef.current.onHide(status);
        }
    }, [status]);
    return (
        <WidgetContentContainer>
            {isConfettiNeeded ? (
                <NewLoanInProgressConfettiWrapper>
                    <Confetti />
                </NewLoanInProgressConfettiWrapper>
            ) : null}
            <TrackingItems
                {...props2}
                logApiEvent={apiEvents}
                logUserEvent={userEvents}
                ref={eSignRef}
                handleOnTokenResponse={handleTokenResponse}
            />
            <PreviewLink type="hidden" href={fileSrc} ref={inputRef} target="blank" />
        </WidgetContentContainer>
    );
};
